import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { useSession } from 'next-auth/react';
import axiosClient from '@/_shared/services/axios-client';

export interface UserPreferenceRecord<T = unknown> {
  value: T;
  updatedAt: string | null;
}

interface ApiPreferenceItem {
  scope: string;
  key: string;
  value: unknown;
  updatedAt: string | null;
}

interface ApiListResponse {
  data: ApiPreferenceItem[];
}

interface ApiSaveResponse {
  data: ApiPreferenceItem;
}

const API_PATH = '/api/user-preferences';

function itemsToMap(
  items: ApiPreferenceItem[]
): Record<string, UserPreferenceRecord> {
  const out: Record<string, UserPreferenceRecord> = {};
  for (const it of items) {
    out[it.key] = { value: it.value, updatedAt: it.updatedAt };
  }
  return out;
}

async function fetchPreferences(
  scope: string
): Promise<Record<string, UserPreferenceRecord>> {
  const response = await axiosClient.get<ApiListResponse>(API_PATH, {
    params: { scope },
  });
  return itemsToMap(response.data?.data ?? []);
}

async function putPreference(
  scope: string,
  key: string,
  value: unknown
): Promise<UserPreferenceRecord> {
  const response = await axiosClient.put<ApiSaveResponse>(API_PATH, {
    scope,
    key,
    value,
  });
  const item = response.data?.data;
  return {
    value: item?.value ?? value,
    updatedAt: item?.updatedAt ?? null,
  };
}

interface StoreEntry {
  data: Record<string, UserPreferenceRecord> | null;
  isLoading: boolean;
  isError: boolean;
  loadPromise: Promise<void> | null;
  listeners: Set<() => void>;
}

const stores = new Map<string, StoreEntry>();
const EMPTY_DATA: Record<string, UserPreferenceRecord> = Object.freeze({});

function storeKey(scope: string, userId: string | number): string {
  return `${scope}::${userId}`;
}

function getOrCreateEntry(key: string): StoreEntry {
  let entry = stores.get(key);
  if (!entry) {
    entry = {
      data: null,
      isLoading: false,
      isError: false,
      loadPromise: null,
      listeners: new Set(),
    };
    stores.set(key, entry);
  }
  return entry;
}

function notify(entry: StoreEntry): void {
  entry.listeners.forEach((listener) => listener());
}

function ensureLoaded(key: string, scope: string): Promise<void> {
  const entry = getOrCreateEntry(key);
  if (entry.data !== null && !entry.isError) return Promise.resolve();
  if (entry.loadPromise) return entry.loadPromise;

  entry.isLoading = true;
  entry.isError = false;
  notify(entry);

  const promise = (async () => {
    try {
      const initial = await fetchPreferences(scope);
      entry.data = initial;
      entry.isError = false;
    } catch (error) {
      entry.isError = true;
      // eslint-disable-next-line no-console
      console.warn(
        `[useUserPreferences] failed to load preferences for scope="${scope}"`,
        error
      );
    } finally {
      entry.isLoading = false;
      entry.loadPromise = null;
      notify(entry);
    }
  })();

  entry.loadPromise = promise;
  return promise;
}

async function setPreferenceInStore(
  key: string,
  scope: string,
  prefKey: string,
  value: unknown
): Promise<void> {
  const entry = getOrCreateEntry(key);
  if (entry.data === null) return; // not yet loaded — callers gate on `isReady`

  // JSON-equality short-circuit: collapse redundant saves (hydration-driven
  // auto-saves that would produce values identical to what we already have).
  let nextSerialized: string | null = null;
  try {
    nextSerialized = JSON.stringify(value ?? null);
  } catch {
    nextSerialized = null;
  }
  const currentRecord = entry.data[prefKey];
  if (currentRecord !== undefined && nextSerialized !== null) {
    let currentSerialized: string | null = null;
    try {
      currentSerialized = JSON.stringify(currentRecord.value ?? null);
    } catch {
      currentSerialized = null;
    }
    if (currentSerialized === nextSerialized) return;
  }

  // Optimistic update.
  entry.data = {
    ...entry.data,
    [prefKey]: { value, updatedAt: new Date().toISOString() },
  };
  notify(entry);

  try {
    const saved = await putPreference(scope, prefKey, value);
    entry.data = {
      ...(entry.data ?? {}),
      [prefKey]: saved,
    };
    notify(entry);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      `[useUserPreferences] failed to save key="${prefKey}" in scope="${scope}"`,
      error
    );
  }
}

// -------------------------------------------------------------------------
// React hook
// -------------------------------------------------------------------------

export interface UseUserPreferencesResult {
  preferences: Record<string, UserPreferenceRecord>;
  getPreference: <T = unknown>(_key: string) => T | undefined;
  setPreference: <T = unknown>(_key: string, _value: T) => Promise<void>;
  isLoading: boolean;
  isError: boolean;
  isReady: boolean;
}

interface Snapshot {
  data: Record<string, UserPreferenceRecord> | null;
  isLoading: boolean;
  isError: boolean;
}

const UNAUTHED_SNAPSHOT: Snapshot = {
  data: null,
  isLoading: false,
  isError: false,
};

export function useUserPreferences(scope: string): UseUserPreferencesResult {
  const { data: session, status } = useSession();
  const userId = session?.user?.id;
  const isAuthed = status === 'authenticated' && !!userId;

  const key = isAuthed && userId ? storeKey(scope, userId) : null;

  // Lazy load — store dedupes concurrent / repeat calls.
  useEffect(() => {
    if (!key || !isAuthed || !userId) return;
    void ensureLoaded(key, scope);
  }, [key, scope, userId, isAuthed]);

  const subscribe = useCallback(
    (listener: () => void) => {
      if (!key) return () => {};
      const entry = getOrCreateEntry(key);
      entry.listeners.add(listener);
      return () => {
        entry.listeners.delete(listener);
      };
    },
    [key]
  );

  const lastSnapshotRef = useRef<Snapshot>(UNAUTHED_SNAPSHOT);
  const lastEntryRef = useRef<StoreEntry | null>(null);

  const getSnapshot = useCallback((): Snapshot => {
    if (!key) {
      lastEntryRef.current = null;
      lastSnapshotRef.current = UNAUTHED_SNAPSHOT;
      return UNAUTHED_SNAPSHOT;
    }
    const entry = getOrCreateEntry(key);
    const prev = lastSnapshotRef.current;
    if (
      lastEntryRef.current === entry &&
      prev.data === entry.data &&
      prev.isLoading === entry.isLoading &&
      prev.isError === entry.isError
    ) {
      return prev;
    }
    const next: Snapshot = {
      data: entry.data,
      isLoading: entry.isLoading,
      isError: entry.isError,
    };
    lastEntryRef.current = entry;
    lastSnapshotRef.current = next;
    return next;
  }, [key]);

  const getServerSnapshot = useCallback((): Snapshot => UNAUTHED_SNAPSHOT, []);

  const snapshot = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot
  );

  const data = snapshot.data;

  const preferences = useMemo(() => data ?? EMPTY_DATA, [data]);

  const getPreference = useCallback(
    <T = unknown>(prefKey: string): T | undefined => {
      const record = data?.[prefKey];
      return record ? (record.value as T) : undefined;
    },
    [data]
  );

  const setPreference = useCallback(
    async <T = unknown>(prefKey: string, value: T): Promise<void> => {
      if (!key || !isAuthed || !userId) return;
      await setPreferenceInStore(key, scope, prefKey, value);
    },
    [key, scope, userId, isAuthed]
  );

  return {
    preferences,
    getPreference,
    setPreference,
    isLoading: snapshot.isLoading,
    isError: snapshot.isError,
    isReady: data !== null,
  };
}

export default useUserPreferences;
